import type { Curriculum, TrieKey } from '@mathflat/domain/Curriculum'
import type { RecommendationBody } from '@mathflat/domain/MakeCustomWorkbook'
import type { OnboardingContent } from '@mathflat/domain/Onboarding'
import type { SchoolType } from '@mathflat/domain/SchoolSystem'
import type { Workbook, WorkbookProblem } from '@mathflat/domain/Workbook'
import type { LineBannerStorageKeyT } from 'src/contentComponents/LineBanner/lineBanner.gate'
import type {
  OriginalOption as WorksheetBuildOriginalOption,
  SimilarOption as WorksheetBuildSimilarOption,
} from 'src/contentComponents/makeLessonMaterial/component/WorksheetBuildOptionPanel/Option.type'
import type { downloadOptions } from 'src/contentComponents/modal/Download/WorksheetDownload.service'
import type {
  MakeWorksheetByArithmeticOption,
  MakeWorksheetByChapterOption,
  MakeWorksheetByPracticeTestOption,
  Printer,
  Student,
  User,
  단원별오답옵션,
} from 'src/dto'
import type { PrintOption } from 'src/dto/Print/PrintOption'
import type { StudentGroupT } from 'src/dto/Student/StudentGroups'
import type { MakeWorksheetPreviewOptionFilter } from 'src/dto/Worksheet/MakeWorksheetPreviewOptionFilter'
import type { WorksheetDesignOption } from 'src/dto/Worksheet/WorksheetDesignOption'
import type { ReportRankOptions } from 'src/page/Lesson/Worksheet/WorksheetPerson/OneClickReportModal/OneClickReportModal'
import type { AuthRoute } from 'src/router'
import type { OnboardingNameT } from 'src/service/popup.service'
import type {
  simplePrintService,
  workbookPrintService,
  worksheetPrintService,
} from 'src/service/print'
import type { TimeStamp } from 'src/types'
import type { StrapiDataT } from 'src/types/common/api'

import type { arithmeticDownloadOptions } from '@/contentComponents/modal/Download/WorksheetDownload.service'
import type { MyListSortType } from '@/controller/myList.controller'
import type { DerivationByWorkbookProblem } from '@/dto/Derivation/derivationParams'
import type { ThemeSchoolTestFilterOptions } from '@/modules/schoolTest/types/filterOptions.type'
import type { AggregatedSecuringType } from '@/modules/schooTest2/util/securingTypeUtils'
import type { PreviousExclusionScope } from '@/types/makeWorksheet'

export const PERSIST_PREFIX = 'PERSIST_'

export const persistentLocalStorageKeys = [
  'SAVED_LOGIN_ID',
  '팝업_만료',
  '업데이트_팝업_만료',
  'ONBOARDING_POPUP_READ_',
  '무료체험_시나리오_만료',
  'DISABLE_LOGIN_PAGE_PROMOTION',
  '팝업_읽은_목록',
  PERSIST_PREFIX,
  'WORKSHEET_MY_LIST_STATE',
  'UPDATE_CONTRACT_MODAL_OPTION',
  'HANDWRITTEN_NOTE_GUIDE_TOOLTIP_VISIBILITY',
  'LIVE_LESSON_PERSON_GUIDE_MODAL_VISIBILITY',
  'LIVE_LESSON_BOARD_GUIDE_MODAL_VISIBILITY',
  'LIVE_LESSON_SCORING_CONFIRM_MODAL_DO_NOT_SHOW_AGAIN',
  'DAILY_LEARNING_ONBOARDING_DO_NOT_SHOW_AGAIN',
  'UPDATED_CONTENTS',
  'IS_MAKE_LIMITED_WIDTH',
  'ACADEMY_INFO_UPDATE_BANNER_CLOSED_AT',
  'QR_SCAN_BUTTON_SEEN',
] as (keyof LocalStorageDataT)[]

// @see 문서: https://www.notion.so/freewheelin/bda97550def44bcbb26e4df9a3d3bcb5
// 로컬스토리지 값 업데이트 할 때마다 문서작성 부탁드립니다.
export type LocalStorageDataT = {
  // string
  SAVED_LOGIN_ID: string

  // DO_NOT_SHOW_AGAIN
  페이지샵_안내사항: 'DO_NOT_SHOW_AGAIN'

  // v2
  'x-auth-token': string // 일렉트론 공유 값
  'print-api': string // 일렉트론 공유 값
  'teacher-id': string // 일렉트론 공유 값

  ASSIGN_TYPE: StudentGroupT['type']
  LOGIN_PATH: AuthRoute
  DEV_API: 'dev' | 'stg' | string
  DEV_API_SECONDARY_ENV: 'dev' | 'stg' | 'live'
  SMS_RECIPIENT: '학부모' | '학생'
  SMS_TYPE: '학년' | '반'
  RECENT_PRINT_NAME: string
  ACADEMY_ID: string
  CREVERSE_SSO_TOKEN: string

  DO_NOT_SHOW_LINE_BANNER_KEYS: string[]

  // boolean
  서술형채점_안내숨김: boolean
  학습지다운로드_하나의PDF_여부: boolean
  학습지다운로드_학생이름표시_여부: boolean
  내학습지_오답학습지제외: boolean
  SHOW_PROBLEM_DRAG_GUIDE: boolean
  MODAL_STUDENT_OPEN_STATE: boolean
  FOLDING: boolean
  HIDE_FREETRIAL_SPEACH_BUBBLE: boolean
  VIEW_WORKBOOK_AS_IMAGE: boolean // [교재 이미지로 보기] 토글에서 사용
  PODMALL_PARENT_PAYMENT_NOTICE_CLOSED: boolean
  PODMALL_PARENT_PAYMENT_MODAL_DESCRIPTION_CLOSED: boolean
  PODMALL_PARENT_PAYMENT_MODAL_STUDENT_DRAWER_TIP_POPOVER_CLOSED: boolean

  KMM_SEP_HIGH_SCHOOL_EXAM_LINE_BANNER_CLOSED: boolean

  // Date (반드시 timestamp로 저장)
  TODAY_FIRST_USE_HISTORY: TimeStamp
  BOTTOM_BANNER_OFF_EXPIRES_AT: TimeStamp
  ACADEMY_INFO_UPDATE_BANNER_CLOSED_AT: TimeStamp
  APP_LAST_ACTIVE_TIMESTAMP: TimeStamp // 앱 마지막 활성 시각(타임스탬프)
  APP_LAST_AUTO_REDIRECT_TIMESTAMP: TimeStamp // 마지막 자동 이동 시각(타임스탬프)
  LINE_BANNER_CLOSED_AT: Partial<Record<LineBannerStorageKeyT, TimeStamp>>

  IORAD_ONBOARDING_SKIP_TIMESTAMP_LIST: {
    onboardingId: OnboardingContent['id']
    timestamp: TimeStamp
  }[]

  // number
  VERSION: number

  // object
  RECENT_WORKBOOK_PRINT_UI_OPTION: typeof workbookPrintService.uiStore
  RECENT_WORKSHEET_PRINT_UI_OPTION: typeof worksheetPrintService.uiStore
  RECENT_SIMPLE_PRINT_UI_OPTION: typeof simplePrintService.uiStore
  WORKSHEET_DOWNLOAD_CHECKED_OPTIONS: (keyof typeof downloadOptions)[]
  WORKSHEET_DOWNLOAD_ARITHMETIC_CHECKED_OPTIONS: (keyof typeof arithmeticDownloadOptions)[]
  RECENT_COMMON_PRINT_OPTION: PrintOption
  팝업_만료: PopupLocalStorageData[]
  업데이트_팝업_만료: PopupLocalStorageData[]
  WORKSHEET_DESIGN_OPTION: LocalDesignOptionT
  DERIVATION_DESIGN_OPTION: WorksheetDesignOption
  BOTTOM_BANNER_ON: BannerToggleT
  REPORT_RANK_OPTIONS: ReportRankT
  WORKBOOK_CLINIC_OPTION: Partial<DerivationByWorkbookProblem>
  BY_SCHOOL_TEST_OPTION: Partial<{
    schoolType: Exclude<SchoolType, 'ELEMENTARY'>
    curriculumKey: string // 중학교용
    highSchoolCurriculumKey: string // 고등학교용
  }>
  BY_CHAPTER_OPTION: ByChapterOptionT
  BY_ARITHMETIC_OPTION: ByArithmeticOptionT
  /** 로그아웃 시 스토리지가 비워지는 것에 기대어 "재로그인까지 닫힘 유지"를 구현한 키 */
  BY_ARITHMETIC_REVISION_NOTICE_CLOSED: boolean
  WRONG_ANSWER_BY_CHAPTER_OPTION: 단원별오답옵션T
  WORKSHEET_BUILD_OPTION_ORIGINAL: WorksheetBuildOriginalOption
  WORKSHEET_BUILD_OPTION_SIMILAR: WorksheetBuildSimilarOption
  GROUPING_GUIDE_BANNER_HIDDEN: boolean
  BY_PRACTICE_OPTION: ByPracticeOptionT
  BY_FAVORITE_OPTION: ByFavoriteOptionT
  // "나의 DB 문제 포함" 체크박스 — 최종 생성 시점 값. 미저장 시 디폴트 ON
  MY_DB_SIMILAR_RECOMMEND_TOGGLE: boolean
  THEME_SCHOOL_TEST_FILTER_OPTIONS: Partial<ThemeSchoolTestFilterOptions>

  시중교재_예시문항보기_여부: boolean

  SELECTED_PRINTER: Printer

  QR_SCAN_FLOATING_ACTION_BUTTON_VISIBILITY: boolean
  CUSTOMER_MESSENGER_VISIBILITY: boolean
  POPUP_VISIBILITY_DEV_ONLY: boolean
  무료체험_시나리오_만료: Record<User['loginId'], TimeStamp>

  HANDWRITTEN_NOTE_GUIDE_TOOLTIP_VISIBILITY: boolean
  LIVE_LESSON_PERSON_GUIDE_MODAL_VISIBILITY: boolean
  LIVE_LESSON_BOARD_GUIDE_MODAL_VISIBILITY: boolean
  LIVE_LESSON_SCORING_CONFIRM_MODAL_DO_NOT_SHOW_AGAIN: boolean

  STUDENT_WORKSHEET_BOARD_TOOLTIP_OPENED: {
    expiredDate: TimeStamp
    wasClosed: boolean
  }
  STUDENT_WORKSHEET_LIST_TOOLTIP_OPENED: {
    expiredDate: TimeStamp
    wasClosed: boolean
  }
  WORKBOOK_SCORING_TOOLTIP_OPENED: {
    expiredDate: TimeStamp
    wasClosed: boolean
  }
  WORKBOOK_BOARD_TOOLTIP_OPENED: {
    expiredDate: TimeStamp
    wasClosed: boolean
  }
  SCHOOLFLAT_TEACHER_GUIDE_TOOLTIP_CLOSED_AT: TimeStamp
  DEBUG_ALLOW_PDF_CLIENT_TO_SHOW_ALL_PAGES: boolean
  DEBUG_PDF_CLIENT_ADDRESS: string
  안내문자전송_무료체험자_가이드_툴팁: boolean
  DISABLE_LOGIN_PAGE_PROMOTION: boolean
  COUPON_TOOLTIP_OPENED: boolean

  KMM_SPEECH_BUBBLE_CLOSED_KEY: ('REGISTER' | 'GRADING' | 'AWARD' | 'APPLY_MATERIALS')[]

  학교별기출학습지_학습지만들기: Partial<{
    filterId: string | null
    Sch_Exam_Region: string
    Sch_Exam_School: string
    Sch_Exam_Grade: string
    Sch_Exam_Year: string
    Sch_Exam_TwinsSimilar: string
    Sch_Exam_Level: string
    Sch_Exam_Count: number
    Sch_Exam_Temple: string
    Sch_Exam_SecuringType: AggregatedSecuringType
  }>

  UPLOAD_LOGO_IMAGE_NOTICE_DO_NOT_SHOW_AGAIN: {
    teacherId: User['teacherId']
  }
  CONFIRM_SCHOOL_TEST_PDF_ACCESS_DO_NOT_SHOW_AGAIN_THIS_MONTH: TimeStamp

  ANSWER_VIDEO_BANNER_CLOSED: {
    teacherId: User['teacherId']
    closedAt: TimeStamp
  }

  WORKBOOK_PREPARATION_BANNER_CLOSED: {
    teacherId: User['teacherId']
    closedAt: TimeStamp
  }
  PAPER_UPLOAD_BANNER_CLOSED: {
    teacherId: User['teacherId']
    closedAt: TimeStamp
  }

  ESSAY_AI_SCORING_BANNER_CLOSED: {
    teacherId: User['teacherId']
    closedAt: TimeStamp
  }

  /** 기간이 아니라 로그인이 해제 시점이라 closedAt 을 두지 않는다 */
  ESSAY_AI_SCORING_GUIDE_MODAL_CLOSED: {
    teacherId: User['teacherId']
  }

  PROBLEM_VIDEO_BANNER_CLOSED: {
    teacherId: User['teacherId']
    closedAt: TimeStamp
  }

  // 내 학습지 페이지 B2G(스쿨플랫) 이벤트 유도 배너 닫힘 상태
  B2G_EVENT_BANNER_CLOSED: {
    closedAt: TimeStamp
    isPermanentClosed: boolean // true: 영구 비노출, false: 2주 후 재노출
  }

  WORKSHEET_MY_LIST_STATE: {
    isOpen: boolean
    sortType: MyListSortType
  }
  학습지_2단계_수정_DO_NOT_SHOW_AGAIN: {
    teacherId: User['teacherId']
  }
  자동시험_수정_DO_NOT_SHOW_AGAIN: {
    teacherId: User['teacherId']
  }
  학습지_3단계_수정_DO_NOT_SHOW_AGAIN: {
    teacherId: User['teacherId']
  }
  DAILY_LEARNING_SELECTED_STUDENT_GROUP_ID: StudentGroupT['id'] | null
  DAILY_LEARNING_SELECTED_STUDENT_ID: Student['id'] | null
  LAST_CREATED_FILTER_BY_SUPPORTED_WORKBOOK: (
    | Omit<
        RecommendationBody<'WORKBOOK_PROBLEM_CHAPTER'>,
        | 'originSupportedWorkbookId'
        | 'originSupportedWorkbookProblemIds'
        | 'groupingChapterType'
        | 'limitProblemCountPerConcept'
        | 'customWorkbookType'
        | 'exampleProblemIds'
      >
    | Omit<
        RecommendationBody<'WORKBOOK_PROBLEM_STEP'>,
        | 'originSupportedWorkbookId'
        | 'originSupportedWorkbookProblemIds'
        | 'steps'
        | 'level'
        | 'customWorkbookType'
        | 'exampleProblemIds'
      >
  ) & {
    groupingChapterType:
      | RecommendationBody<'WORKBOOK_PROBLEM_CHAPTER'>['groupingChapterType']
      | null
    limitProblemCountPerConcept:
      | RecommendationBody<'WORKBOOK_PROBLEM_CHAPTER'>['limitProblemCountPerConcept']
      | undefined
    steps: RecommendationBody<'WORKBOOK_PROBLEM_STEP'>['steps'] | null
    level: RecommendationBody<'WORKBOOK_PROBLEM_STEP'>['level'] | null
    includeSameProblem?: boolean
    // 기존 출제 문제 제외 기준(학생 대상 studentIds 는 영속화하지 않음)
    previousExclusionScope?: PreviousExclusionScope
  }
  LAST_CREATED_FILTER_BY_SUPPORTED_WORKBOOK_ID: {
    [
      originSupportedWorkbookId: Workbook['id']
    ]: LocalStorageDataT['LAST_CREATED_FILTER_BY_SUPPORTED_WORKBOOK'] & {
      originSupportedWorkbookId: Workbook['id']
      originSupportedWorkbookProblemIds: WorkbookProblem['id'][]
    }
  }
  SETTLEMENT_INFO_DRAFT: {
    academyId: string
    name: string
    phoneNumber: string
    birthDate: string
  }
  UPDATE_CONTRACT_MODAL_OPTION: {
    contractStartDate: TimeStamp
    teacherIds: User['teacherId'][]
  }
  학교별기출시험지범위안내: { teacherId: User['teacherId'] }

  UPDATED_CONTENTS: {
    id: number
    showCount: number
    version: 'V1'
  }[]

  IS_MAKE_LIMITED_WIDTH: boolean
  /** GNB QR 버튼을 한 번 눌러 봤는지. 처음 알림 모션을 다시 돌리지 않으려고 로그아웃에도 남긴다 */
  QR_SCAN_BUTTON_SEEN: boolean
  DAILY_LEARNING_ONBOARDING_DO_NOT_SHOW_AGAIN: boolean
  MIXPANEL_DEBUG: boolean
  CONTRACT_TOOLTIP_CLOSED_AT: TimeStamp
  WORKSHEET_SHARE_CURRICULUM_FILTER: {
    schoolType?: SchoolType
    trieKey?: TrieKey | 'ALL'
  }
  LECTURE_ACCORDION_CLOSED_GROUPS: Record<TrieKey, Curriculum['id'][]>
} & SuffixResult &
  PrefixPersist<'COVER_EDITOR_NEW_FONT', { teacherId: User['teacherId'] }> &
  PrefixPersist<'LIMITED', boolean> &
  PrefixPersist<
    `DO_NOT_SHOW_AGAIN_NOTICE_BOARD_${User['teacherId']}`,
    { teacherId: User['teacherId']; expiredAt: TimeStamp }
  > &
  PrefixPersist<'ALREADY_REPORTED_ANSWER_NOT_CONFIRMED_PUBLIC_WORKBOOK_IDS', Workbook['id'][]>

// type SuffixOption = Suffix<'OPTION', number>
type Suffix<T extends string, V> = {
  [key in `${T}${string}`]: V
}

type PrefixPersistKey<T extends string> = `${typeof PERSIST_PREFIX}${T}`
type PrefixPersist<T extends string, V> = {
  [key in PrefixPersistKey<T>]: V
}

type SuffixOnboardingPopupRead = Suffix<'ONBOARDING_POPUP_READ_', OnboardingNameT[]>

// Suffix Type 모으기
type SuffixResult = SuffixOnboardingPopupRead

export type PopupLocalStorageData = {
  id: StrapiDataT['id']
  expiredDate: TimeStamp | 'infinite'
}

type ByPracticeOptionT = Omit<
  typeof MakeWorksheetByPracticeTestOption.prototype,
  'update' | 'updateExceptNullable' | 'toJS' | 'params'
> & {
  filterId: string
  practiceProblemIdList: unknown[]
  curriculumIdList: unknown[]
  problemList: null
}
type ByFavoriteOptionT = Omit<
  typeof MakeWorksheetByChapterOption.prototype,
  'update' | 'updateExceptNullable' | 'toJS' | 'params' | 'filterParams'
>

type ByChapterOptionT = Omit<
  typeof MakeWorksheetByChapterOption.prototype,
  'update' | 'updateExceptNullable' | 'toJS' | 'params' | 'filterParams'
> & {
  filterId: string
  isEquality: boolean
}

type ByArithmeticOptionT = Omit<
  typeof MakeWorksheetByArithmeticOption.prototype,
  'update' | 'updateExceptNullable' | 'toJS' | 'params'
> & {
  filterId: string
  isEquality: boolean
}

type 단원별오답옵션T = Pick<typeof 단원별오답옵션.prototype.params, 'isFillProblem'>
type LocalDesignOptionT = typeof MakeWorksheetPreviewOptionFilter.prototype.designOptionParams
type BannerToggleT = { value: boolean; timeStamp: number }

type ReportRankT = {
  hasRank: boolean
  options: ReportRankOptions[number]['value'][] | null
}
